import { create } from 'zustand';
import { User, AuthState } from '../types';
import {
  loginUser,
  registerUser,
  logoutUser,
  resetPassword,
  onAuthChange,
} from '../services/authService';
import { toast } from './toastStore';

interface AuthStore extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  initializeAuth: () => () => void;
  _initialized: boolean;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  loading: true,
  error: null,
  _initialized: false,

  setUser: (user) => set({ user }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),

  clearError: () => {
    if (get().error !== null) {
      set({ error: null });
    }
  },

  login: async (email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      const user = await loginUser(email, password);
      set({ user, loading: false });

      // ✅ Success toast
      toast.success(
        'Welcome back! 👋',
        `Signed in as ${user.displayName || user.email}`
      );
    } catch (error: any) {
      const message = getFirebaseErrorMessage(error.code);
      set({ error: message, loading: false });

      // ❌ Error toast
      toast.error('Login failed', message);
      throw error;
    }
  },

  register: async (email: string, password: string, displayName: string) => {
    set({ loading: true, error: null });
    try {
      const user = await registerUser(email, password, displayName);
      set({ user, loading: false });

      // ✅ Success toast
      toast.success(
        'Account created! 🎉',
        `Welcome to VNGPT, ${displayName}!`
      );
    } catch (error: any) {
      const message = getFirebaseErrorMessage(error.code);
      set({ error: message, loading: false });

      // ❌ Error toast
      toast.error('Registration failed', message);
      throw error;
    }
  },

  logout: async () => {
    const userName = get().user?.displayName || get().user?.email || 'User';
    set({ loading: true });
    try {
      await logoutUser();
      set({ user: null, loading: false });

      // ✅ Logout toast
      toast.info('Signed out', `Goodbye, ${userName}! See you soon.`);
    } catch (error: any) {
      set({ error: error.message, loading: false });

      // ❌ Error toast
      toast.error('Logout failed', error.message);
    }
  },

  forgotPassword: async (email: string) => {
    set({ loading: true, error: null });
    try {
      await resetPassword(email);
      set({ loading: false });

      // ✅ Success toast
      toast.success(
        'Reset email sent! 📧',
        `Check your inbox at ${email}`
      );
    } catch (error: any) {
      const message = getFirebaseErrorMessage(error.code);
      set({ error: message, loading: false });

      // ❌ Error toast
      toast.error('Reset failed', message);
      throw error;
    }
  },

  initializeAuth: () => {
    if (get()._initialized) {
      return () => {};
    }
    set({ _initialized: true });

    const unsubscribe = onAuthChange((user) => {
      set({ user, loading: false });
    });
    return () => {
      set({ _initialized: false });
      unsubscribe();
    };
  },
}));

function getFirebaseErrorMessage(code: string): string {
  switch (code) {
    // ─── Registration errors ───
    case 'auth/email-already-in-use':
      return 'This email is already registered. Try logging in instead.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/operation-not-allowed':
      return 'Email/password sign-up is not enabled. Contact support.';
    case 'auth/weak-password':
      return 'Password is too weak. Use at least 6 characters with a mix of letters and numbers.';

    // ─── Login errors ───
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact support for help.';
    case 'auth/user-not-found':
      return 'No account found with this email. Would you like to sign up?';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again or reset your password.';
    case 'auth/invalid-credential':
      return 'Invalid email or password. Please check and try again.';

    // ─── Rate limiting ───
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please wait a few minutes before trying again.';

    // ─── Network ───
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection.';

    // ─── Other ───
    case 'auth/popup-closed-by-user':
      return 'Sign-in popup was closed. Please try again.';
    case 'auth/requires-recent-login':
      return 'Please log in again to complete this action.';

    default:
      return `Authentication error: ${code || 'Unknown error'}. Please try again.`;
  }
}